import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTable } from '@angular/material/table';
import { Router } from '@angular/router';
import { AlertService } from '@services/alert.service';
import { VendorOrderService } from '@services/vendor-order.service';
import { VendorService } from '@services/vendor.service';
import {
  catchError,
  debounceTime,
  EMPTY,
  Subject,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { OrdersData } from 'src/app/v2/common/sidebar/sidebar.component';
import { sortTable } from 'src/app/v2/shared/utils';
import { hasOpenPayables } from 'src/app/v2/shared/utils/payables';

@Component({
  selector: 'app-payables-v2',
  templateUrl: './payables.component.html',
  styleUrls: ['./payables.component.scss'],
})
export class PayablesComponent implements OnInit {
  ordersCount: Array<OrdersData> = [];
  filteredOrdersCount: Array<OrdersData> = [];
  isToggled = false;
  unpaidOrders: number = 0;
  pageSizes: Array<number>;
  defaultPageSizes: Array<number> = [10, 20, 25];
  unsubscribe$ = new Subject<void>();
  searchControl: FormControl;
  showAll: boolean;
  defaultSort: Sort = { active: 'name', direction: 'asc' };
  displayedColumns: string[] = ['name', 'unpaid', 'paid'];
  sort: Sort;
  dataSource: MatTableDataSource<OrdersData>;
  @ViewChild(MatTable) table: MatTable<OrdersData>;

  constructor(
    private readonly aS: AlertService,
    private readonly vS: VendorService,
    public vendorOrderService: VendorOrderService,
    public cdRef: ChangeDetectorRef,
    public router: Router
  ) {}

  ngOnInit(): void {
    this.sort = this.defaultSort;
    this.searchControl = new FormControl(null);
    this.searchControl.valueChanges
      .pipe(debounceTime(300), takeUntil(this.unsubscribe$))
      .subscribe((searchText: string) => this.searchDataSource(searchText));
    this.getVendorOrders();
  }

  getVendorOrders() {
    const hasCached = !!this.vendorOrderService.getCachedVendorOrderCounts('all');
    if (!hasCached) {
      this.aS.loading.next({ isLoading: true });
    }
    this.unpaidOrders = 0;
    this.vendorOrderService
      .getDistributorVendorOrderCounts('all')
      .pipe(
        tap((ordersData: any) => this.processVendorCounts(ordersData)),
        catchError(() => {
          this.aS.loading.next({ isLoading: false });
          return EMPTY;
        })
      )
      .subscribe({
        complete: () => {
          this.aS.loading.next({ isLoading: false });
          this.getDistributorVendors().pipe(take(1)).subscribe();
        },
        error: () => this.aS.loading.next({ isLoading: false }),
      });
  }

  processVendorCounts(ordersData: any) {
    this.unpaidOrders = 0;
    const data: Record<
      string,
      { name: string; paid: number; unpaid: number }
    > = ordersData.vendorOrderCounts.reduce(
      (acc: Record<string, Partial<OrdersData>>, curr: any) => {
        if (!curr.vendor?._id) {
          return acc;
        }
        const existing = acc[curr.vendor._id];
        this.unpaidOrders += curr.unpaid;
        return {
          ...acc,
          [curr.vendor._id]: {
            name: curr.vendor.name,
            paid: (existing?.paid ?? 0) + curr.paid,
            unpaid: (existing?.unpaid ?? 0) + curr.unpaid,
            awaitingCredit:
              (existing?.awaitingCredit ?? 0) + (curr.awaitingCredit ?? 0),
            distributor: curr.vendor.distributor ?? existing?.distributor ?? false,
            vendorIds: curr.vendor.distributorVendors ?? existing?.vendorIds,
          },
        };
      },
      {}
    );
    this.ordersCount = Object.entries(data).map(([id, value]) => ({
      vendorId: id,
      ...value,
    }));
    this.applyFilters();
    this.pageSizes = Array.from(
      new Set(
        this.defaultPageSizes
          .filter((size) => size <= this.filteredOrdersCount.length)
          .concat([this.filteredOrdersCount.length])
      )
    );
    this.setDataSource();
  }

  setDistributorVendors(vendorNames: Record<string, { name: string }>) {
    this.ordersCount
      .filter((order) => order.distributor)
      .forEach((vendor) => {
        vendor.vendorNames = Object.entries(vendorNames ?? {})
          .filter(([id]) => vendor.vendorIds?.includes(id))
          .map(([, data]) => data?.name ?? '');
      });
  }

  getDistributorVendors() {
    const vendorIds = this.ordersCount.reduce(
      (acc: Array<string>, curr: OrdersData) => {
        if (!curr.distributor) {
          return acc;
        }
        return [...acc, ...curr.vendorIds];
      },
      []
    );

    return this.getDistributorVendorNames(vendorIds).pipe(
      tap(() => {
        if (this.searchControl?.value?.trim()) {
          this.applyFilters();
        } else {
          this.setDataSource();
        }
      })
    );
  }

  getDistributorVendorNames(vendorIds: Array<string>) {
    return this.vS.getVendorNames(vendorIds).pipe(
      take(1),
      tap((res) => this.setDistributorVendors(res))
    );
  }

  searchDataSource(vendor: string) {
    if (vendor?.trim()) {
      this.showAll = true;
    }
    this.applyFilters();
  }

  applyFilters() {
    const search = this.searchControl?.value?.trim().toLowerCase() ?? '';
    let orders = this.ordersCount.filter(
      (order) => this.showAll || hasOpenPayables(order)
    );

    if (search.length) {
      orders = orders.filter(
        (order) =>
          order.name?.toLowerCase().includes(search) ||
          order.vendorNames?.some(
            (name) => name?.length && name.toLowerCase().includes(search)
          )
      );
    }

    this.filteredOrdersCount = sortTable(this.sort, orders);
    this.setDataSource();
  }

  setDataSource() {
    if (this.table) {
      this.dataSource = new MatTableDataSource(this.filteredOrdersCount);
      this.cdRef.detectChanges();
    }
  }

  sortData(sort?: Sort) {
    this.sort = sort ?? this.defaultSort;
    this.applyFilters();
  }

  viewVendorOrders(vendorId: string, isDistributor?: boolean) {
    const routeConfig = isDistributor
      ? ['/v2/payables/distributor', vendorId]
      : ['/v2/payables/vendor', vendorId];
    this.router.navigate(routeConfig);
  }

  viewType() {
    this.applyFilters();
  }
}
